'use client'

import { Chevron } from '@/components/svg/chevron/chevron'
import { LearnMoreSVG } from '@/components/svg/learn-more/learn-more'
import { Image } from '@/components/v2/atoms/image/image'
import { Button } from '@/components/v3/atoms/button/button'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import { useInView } from '@/hooks/use-in-view'
import cx from 'classnames'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import Link from 'next/link'
import { useRef, useState } from 'react'
import s from './card-image-scroller.module.scss'

type Props = {
  /** Hero image first, then up to 4 gallery images (max 5 total). */
  images: string[]
  alt: string
  href: string
  width: number
  height: number
  heroLoading?: 'lazy' | 'eager'
  /** Mobile layout of the parent card. 'vertical' shows a single static image
   *  on mobile (no slider/dots); desktop always gets the slider. */
  mobileVariant?: 'landscape' | 'vertical'
}

/**
 * Card image area. Renders a static hero image by default (SSR). Once the card
 * enters the viewport it progressively enhances into a slider (hero + gallery):
 * swipeable with dot indicators on mobile, and on desktop the dots and prev/next
 * chevron buttons appear on hover. The keen-slider instance and gallery images
 * are not created until then.
 */
export const CardImageScroller = ({
  images,
  alt,
  href,
  width,
  height,
  heroLoading = 'lazy',
  mobileVariant = 'landscape',
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const breakpoint = useBreakpoint()
  const hasBeenInView = useInView(wrapperRef, { once: true })
  const [current, setCurrent] = useState(0)

  // The vertical mobile layout shows a single static image (no slider/dots);
  // every other case (landscape mobile, and all of desktop) gets the slider.
  const showStaticOnly = breakpoint === 'mobile' && mobileVariant === 'vertical'
  const hasMultiple = images.length > 1
  const enhanced = !showStaticOnly && hasMultiple && hasBeenInView

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    renderMode: 'performance',
    initial: 0,
    slides: { perView: 1, spacing: 0 },
    slideChanged: (slider) => setCurrent(slider.track.details.rel),
  })

  return (
    <div
      ref={wrapperRef}
      className={cx(s.scroller, { [s.enhanced]: enhanced })}
    >
      {enhanced ? (
        <>
          <div ref={sliderRef} className={cx('keen-slider', s.track)}>
            {images.map((url, index) => (
              <Link
                href={href}
                key={`${url}-${index}`}
                className={cx('keen-slider__slide', s.slide)}
                aria-label={alt}
                tabIndex={index === 0 ? undefined : -1}
                draggable={false}
              >
                <Image
                  src={url}
                  alt={index === 0 ? alt : ''}
                  width={width}
                  height={height}
                  fit
                  fill
                  draggable={false}
                  loading={index === 0 ? heroLoading : 'lazy'}
                  className={s.image}
                />
              </Link>
            ))}
          </div>

          <div className={s.dots}>
            {images.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Go to image ${index + 1} of ${images.length}`}
                aria-current={current === index}
                className={cx(s.dot, { [s.dotActive]: current === index })}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  instanceRef.current?.moveToIdx(index)
                }}
              />
            ))}
          </div>

          {/* Desktop-only prev/next controls; shown on hover via CSS. */}
          <div className={cx(s.chevron, s.chevronLeft)}>
            <Button
              variant="primary"
              className={s.chevronBtn}
              aria-label="Previous image"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                instanceRef.current?.prev()
              }}
            >
              <Chevron direction="left" />
            </Button>
          </div>
          <div className={cx(s.chevron, s.chevronRight)}>
            <Button
              variant="primary"
              className={s.chevronBtn}
              aria-label="Next image"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                instanceRef.current?.next()
              }}
            >
              <Chevron direction="right" />
            </Button>
          </div>
        </>
      ) : (
        <Image
          src={images[0]}
          alt={alt}
          width={width}
          height={height}
          fit
          fill
          loading={heroLoading}
          className={s.image}
        />
      )}

      {/* Orange tint + "Learn more" on hover. Sits above the image but below the
          dots/chevrons (higher z within the scroller) and the card's
          website/instagram buttons (above the whole scroller). */}
      <div className={s.hoverOverlay} aria-hidden>
        <LearnMoreSVG className={s.learnMore} />
      </div>
    </div>
  )
}
